import { createSignal } from "solid-js";
import type { NotificationDto } from "../api";
import { NotificationKind, NotificationResourceFamily } from "../api/generated/schemas";

export type Translate = (key: string, ...args: (string | number)[]) => string;

export type NotificationTone = "warning" | "error";

const KIND_TEXT: Record<NotificationKind, {
  tone: NotificationTone;
  title: string;
  body: string;
  bodyArgs: (n: NotificationDto) => string[];
}> = {
  [NotificationKind.NUMBER_0]: {
    tone: "warning",
    title: "NOTIFICATION_PLOT_USE_DENIED",
    body: "NOTIFICATION_PLOT_USE_DENIED_BODY",
    bodyArgs: (n) => [n.args["chest"] ?? "", n.resourceName],
  },
  [NotificationKind.NUMBER_1]: {
    tone: "error",
    title: "NOTIFICATION_CRAFT_ORDER_FAILED",
    body: "NOTIFICATION_CRAFT_ORDER_FAILED_BODY",
    bodyArgs: (n) => [n.args["item"] ?? "", n.args["quantity"] ?? "", n.args["reason"] ?? ""],
  },
  [NotificationKind.NUMBER_2]: {
    tone: "warning",
    title: "NOTIFICATION_STOCK_LOW",
    body: "NOTIFICATION_STOCK_LOW_BODY",
    bodyArgs: (n) => [n.args["item"] ?? "", n.args["onHand"] ?? "", n.args["min"] ?? ""],
  },
  [NotificationKind.NUMBER_3]: {
    tone: "error",
    title: "NOTIFICATION_STOCK_EMPTY",
    body: "NOTIFICATION_STOCK_EMPTY_BODY",
    bodyArgs: (n) => [n.args["item"] ?? "", n.args["onHand"] ?? "", n.args["min"] ?? ""],
  },
};

const FAMILY_ICON: Record<NotificationResourceFamily, string> = {
  [NotificationResourceFamily.NUMBER_0]: "fa-robot",
  [NotificationResourceFamily.NUMBER_1]: "fa-warehouse",
  [NotificationResourceFamily.NUMBER_2]: "fa-user",
};

export function notificationText(n: NotificationDto, t: Translate): { title: string; body: string } {
  const kindText = KIND_TEXT[n.kind];
  return { title: t(kindText.title, n.resourceName), body: t(kindText.body, ...kindText.bodyArgs(n)) };
}

export function notificationTone(n: NotificationDto): NotificationTone {
  return KIND_TEXT[n.kind].tone;
}

export function notificationIcon(n: NotificationDto): string {
  return FAMILY_ICON[n.resourceFamily];
}

export function notificationLink(n: NotificationDto): string | undefined {
  switch (n.resourceFamily) {
    case NotificationResourceFamily.NUMBER_0: return `/bot/${n.resourceId}`;
    case NotificationResourceFamily.NUMBER_1: return `/inventory/storages#${n.resourceId}/stock`;
    default: return undefined;
  }
}

const [open, setOpen] = createSignal<boolean>(false);
const [notifications, setNotifications] = createSignal<NotificationDto[]>([]);

export function unreadCount(): number {
  return notifications().filter((n) => !n.isRead).length;
}

export function useNotifications() {
  return {
    notifications,
    setNotifications,
    open,
    setOpen,
    unreadCount,
  };
}
